import { AuthProviderType } from '@/types/auth';
import { Backdrop, Button, CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useNotifications } from '@toolpad/core';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleIcon } from '../../../components/displays/CustomIcons';
import { OnboardingCard } from '../../../components/displays/OnboardingCard';
import { OnboardingContainer } from '../../../components/displays/OnboardingContainer';
import LanguageSelector from '../../../components/inputs/LanguageSelector';
import { PasswordField } from '../../../components/inputs/PasswordField/PasswordField';
import ForgotPassword from './ForgotPassword';

export type Credential = { username: string; password: string };

export default function SignIn(props: {
    onSignUp?: () => void;
    onSignIn?: (provider: AuthProviderType, credential?: Credential, remember?: boolean) => Promise<{ error?: string }>;
    onRequestResetPassword?: (email: string) => Promise<void>;
    loading?: boolean;
    defaultEmail?: string;
}) {
    const { t, i18n } = useTranslation();
    const [email, setEmail] = React.useState(props.defaultEmail || '');
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [passwordError, setPasswordError] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
    const [open, setOpen] = React.useState(false);
    const [signingIn, setSigningIn] = React.useState<AuthProviderType | undefined>(undefined);
    const [remember, setRemember] = React.useState(!!props.defaultEmail);

    const notifications = useNotifications();

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (emailError || passwordError) {
            return;
        }

        const data = new FormData(event.currentTarget);
        const email = data.get('email') as string;
        const password = data.get('password') as string;

        if (props.onSignIn) {
            setSigningIn('credential');
            const result = await props.onSignIn('credential', { username: email, password: password }, remember).finally(() => {
                setSigningIn(undefined);
            });

            if (result.error) {
                notifications.show(result.error, { severity: 'error', autoHideDuration: 5000 });
                setEmailError(true);
                setPasswordError(true);
                setPasswordErrorMessage(t('welcome.signIn.invalidCredentials'));
            }
        }
    };

    const validateInputs = () => {
        const email = document.getElementById('email') as HTMLInputElement;
        const password = document.getElementById('password') as HTMLInputElement;

        let isValid = true;

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
            setEmailError(true);
            setEmailErrorMessage(t('common.form.invalidEmail'));
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        if (!password.value || password.value.length < 8) {
            setPasswordError(true);
            setPasswordErrorMessage(t('common.form.invalidPassword'));
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        return isValid;
    };

    return (
        <OnboardingContainer direction='column' justifyContent='space-between'>
            <OnboardingCard variant='outlined' style={{ position: 'relative' }}>
                <Stack direction='row' sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography component='h1' variant='h4' sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}>
                        {t('common.signIn')}
                    </Typography>
                    <LanguageSelector onChange={(locale) => i18n.changeLanguage(locale)} />
                </Stack>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button loading={signingIn === 'google'} fullWidth variant='outlined' onClick={async () => props.onSignIn && (await props.onSignIn('google'))} startIcon={<GoogleIcon />}>
                        {t('welcome.oauth.google')}
                    </Button>
                    {/* <Button loading={signingIn === 'clever'} fullWidth variant='outlined' onClick={async () => props.onSignIn && (await props.onSignIn('clever'))} startIcon={<CleverIcon />}>
                        {t('welcome.oauth.clever')}
                    </Button> */}
                </Box>
                <Divider>{t('welcome.or')}</Divider>
                <Box
                    component='form'
                    onSubmit={handleSubmit}
                    noValidate
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        gap: 2,
                    }}
                >
                    <FormControl>
                        <FormLabel htmlFor='email'>{t('common.form.email')}</FormLabel>
                        <TextField
                            error={emailError}
                            helperText={emailErrorMessage}
                            id='email'
                            type='email'
                            name='email'
                            placeholder='your@email.com'
                            autoComplete='email'
                            autoFocus
                            required
                            fullWidth
                            variant='outlined'
                            color={emailError ? 'error' : 'primary'}
                            value={email}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setEmail(event.target.value);
                            }}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel htmlFor='password'>{t('common.form.password')}</FormLabel>
                        <PasswordField
                            error={passwordError}
                            helperText={passwordErrorMessage}
                            name='password'
                            placeholder='••••••'
                            id='password'
                            autoComplete='current-password'
                            required
                            fullWidth
                            label={undefined}
                            variant='outlined'
                            color={passwordError ? 'error' : 'primary'}
                            value={password}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setPassword(event.target.value);
                            }}
                        />
                    </FormControl>
                    <Stack direction='row' sx={{ justifyContent: 'space-between' }}>
                        <FormControlLabel
                            control={<Checkbox value='remember' color='primary' onChange={(e) => setRemember(e.target.checked)} defaultChecked={!!props.defaultEmail} />}
                            label={t('welcome.remember')}
                        />
                        <Link component='button' type='button' onClick={handleClickOpen} variant='body2' sx={{ alignSelf: 'center' }}>
                            {`${t('common.forgotPassword')}?`}
                        </Link>
                    </Stack>
                    <ForgotPassword open={open} onClose={handleClose} onContinue={props.onRequestResetPassword} />
                    <Button
                        loading={signingIn === 'credential'}
                        type='submit'
                        id='signInButton'
                        fullWidth
                        variant='contained'
                        onClick={validateInputs}
                        loadingIndicator={<CircularProgress size={16} />}
                    >
                        {t('common.signIn')}
                    </Button>
                    <Typography sx={{ textAlign: 'center' }}>
                        {t('welcome.signIn.noAccount')}{' '}
                        <Link component='button' onClick={props.onSignUp} variant='body2' sx={{ verticalAlign: 'baseline' }}>
                            {t('common.signUp')}
                        </Link>
                    </Typography>
                </Box>
                <Backdrop style={{ position: 'absolute' }} open={!!props.loading}>
                    <CircularProgress color='inherit' />
                </Backdrop>
            </OnboardingCard>
        </OnboardingContainer>
    );
}
